import React, {useState, useRef, useEffect, useMemo, useCallback} from 'react';
import uuid from 'react-native-uuid';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  Image,
  Alert,
  useColorScheme,
  Animated,
  Easing,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import {useScreenTheme} from '../../../shared/hooks/useScreenTheme';
import {createNovaAIChatWindowStyles} from '../../../shared/components/NovaAI/NovaAIChatWindow.styles';
import {Input} from '../../../shared/components/ui';
import {SendIcon} from '../../../shared/assets/icons/SendIcon';
import {MicIcon} from '../../../shared/assets/icons/MicIcon';
import {NovaVoiceModule} from '../../../shared/nativeModules/NovaVoiceModule';
import {useNovaAIChatWindowController} from '../../../shared/components/NovaAI/NovaAIChatWindowController';
import type {NovaStage} from '../../../shared/components/NovaAI/NovaAIChatWindowController';
import {useSpeechToText} from '../../../shared/hooks/useSpeechToText';
import {getConfig} from '../../../shared/config/AppConfig';
import {useNovaAINavigation} from '../../../shared/components/NovaAI/useNovaAINavigation';
import {useNovaDataRefresh} from '../../../shared/components/NovaAI/useNovaDataRefresh';
import {useDispatch} from 'react-redux';
import {
  ChatMsg,
  NovaAIChatWindowProps,
  Status,
  SuggestedQuestion,
  VoiceWaveformBubbleProps,
} from '../../../shared/components/NovaAI/NovaAIProps';
import {
  novaAICardData,
  statuses,
  userTypes,
  WAVEFORM_BAR_COUNT,
  WAVEFORM_STAGGER_MS,
  WAVEFORM_STAGGER_CYCLE_MS,
  WAVEFORM_BASE_DURATION_MS,
  WAVEFORM_DURATION_STEP_MS,
  WAVEFORM_DURATION_STEP_MOD,
  WAVEFORM_SCALE_MAX,
  WAVEFORM_SCALE_MIN,
  WAVEFORM_SCALE_INITIAL,
  WAVEFORM_STATIC_PATTERN,
  NOVA_CAPABILITIES,
} from '../NovaAI/constants/novaAI.constant';
import NovaAICollapseIcon from '@shared/assets/icons/NovaAICollapseIcon';
import CloseIcon from '../../../shared/assets/icons/CloseIcon';
import {useTranslation} from 'react-i18next';
import {
  checkMicrophonePermission,
  requestMicrophonePermission,
} from '../../../shared/components/NovaAI/permissions';
import {RESULTS} from 'react-native-permissions';
import {NovaAIChatBotIcon} from '@shared/assets/icons/NovaAIChatBotIcon';
import {NovaAIChatWindowIcon} from '@shared/assets/icons/NovaAIChatWindowIcon';
import BotStructuredMessage from '../../../shared/components/NovaAI/BotStructuredMessage';
import NovaAIVolumeIcon from '@shared/assets/icons/NovaAIVolumeIcon';
import NovaAIVolumeMuteIcon from '@shared/assets/icons/NovaAIVolumeMuteIcon';
import NovaAIExpandIcon from '@shared/assets/icons/NovaAIExpandIcon';
import NovaCategoryWrenchIcon from '@shared/assets/icons/NovaCategoryWrenchIcon';
import NovaAIMinimizeExpandIcon from '@shared/assets/icons/NovaAIMinimizeExpandIcon';
import NovaAIMinimizeCollapseIcon from '@shared/assets/icons/NovaAIMinimizeCollapseIcon';
import NovaCategoryWarningIcon from '@shared/assets/icons/NovaCategoryWarningIcon';
import NovaCategoryCheckIcon from '@shared/assets/icons/NovaCategoryCheckIcon';
import NovaCategoryBookIcon from '@shared/assets/icons/NovaCategoryBookIcon';

// ---------------------------------------------------------------------------
// Map iconType → imported icon component
// ---------------------------------------------------------------------------

const CATEGORY_ICON_MAP: Record<
  SuggestedQuestion['iconType'],
  React.FC<{size?: number; color?: string}>
> = {
  wrench: NovaCategoryWrenchIcon,
  warning: NovaCategoryWarningIcon,
  check: NovaCategoryCheckIcon,
  book: NovaCategoryBookIcon,
};

// ---------------------------------------------------------------------------
// Animated voice waveform – shown inside the user's voice message bubble
// ---------------------------------------------------------------------------

const VoiceWaveformBubble: React.FC<VoiceWaveformBubbleProps> = ({
  waveformContainerStyle,
  waveformBarStyle,
  isActive = true,
}) => {
  const anims = useRef(
    Array.from(
      {length: WAVEFORM_BAR_COUNT},
      () => new Animated.Value(WAVEFORM_SCALE_INITIAL),
    ),
  ).current;

  useEffect(() => {
    if (!isActive) {
      // Response received — stop loops and freeze bars into a static wave shape
      anims.forEach((anim, i) => {
        anim.stopAnimation();
        const targetScale =
          WAVEFORM_STATIC_PATTERN[i % WAVEFORM_STATIC_PATTERN.length] ??
          WAVEFORM_SCALE_MIN;
        Animated.timing(anim, {
          toValue: targetScale,
          duration: 250,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }).start();
      });
      return;
    }
    const loops = anims.map((anim, i) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay((i * WAVEFORM_STAGGER_MS) % WAVEFORM_STAGGER_CYCLE_MS),
          Animated.timing(anim, {
            toValue: WAVEFORM_SCALE_MAX,
            duration:
              WAVEFORM_BASE_DURATION_MS +
              (i % WAVEFORM_DURATION_STEP_MOD) * WAVEFORM_DURATION_STEP_MS,
            easing: Easing.inOut(Easing.sin),
            useNativeDriver: true,
          }),
          Animated.timing(anim, {
            toValue: WAVEFORM_SCALE_MIN,
            duration:
              WAVEFORM_BASE_DURATION_MS +
              (i % WAVEFORM_DURATION_STEP_MOD) * WAVEFORM_DURATION_STEP_MS,
            easing: Easing.inOut(Easing.sin),
            useNativeDriver: true,
          }),
        ]),
      ),
    );
    const combined = Animated.parallel(loops);
    combined.start();
    return () => combined.stop();
  }, [anims, isActive]);

  return (
    <View style={waveformContainerStyle}>
      {anims.map((anim, i) => (
        <Animated.View
          key={i}
          style={[waveformBarStyle, {transform: [{scaleY: anim}]}]}
        />
      ))}
    </View>
  );
};

// ---------------------------------------------------------------------------


/**
 * What a technician reads while Nova works.
 *
 * The wording lives here, not in Nova, and the reason is Spanish: this app
 * ships `en` and `es` (with a Spanish truncation rule in i18n.ts), so text
 * composed in the backend would arrive English-only mid-turn. Nova sends
 * vocabulary — a stage, the tool it is about to run, and any ids it resolved
 * for that tool — and the words are chosen and translated here.
 *
 * Every tool has an entry. A tool Nova adds or renames falls through to the
 * generic line rather than showing a technician a raw identifier, so this
 * table can lag the backend without breaking; `named` is used when Nova sent
 * an id worth putting in the sentence, `plain` otherwise.
 *
 * **Named phrases lead with the id, and must keep doing so.** i18n.ts
 * truncates any Spanish string over 40 characters to `substring(0, 40)`, and
 * Spanish runs longer than English: "Reading the tasks on Repair 207541..."
 * becomes "Leyendo las tareas de la reparación 2075..." — a repair number cut
 * in half, which is worse than no number at all. With the id first it survives
 * the cut whoever writes the translation.
 */
type Phrase = {key: string; plain: string; named?: string};

const TOOL_PHRASES: Record<string, Phrase> = {
  // ── reads: the slow ones, where the message actually earns its place ──────
  get_shift_status: {key: 'novaAI.stageShift', plain: 'Checking your shift...'},
  get_repair_tasks: {
    key: 'novaAI.stageTasks',
    plain: 'Reading the tasks...',
    named: 'Repair {{id}} — reading the tasks...',
  },
  get_repair_details: {
    key: 'novaAI.stageRepair',
    plain: 'Opening the repair...',
    named: 'Repair {{id}} — opening...',
  },
  list_my_repairs: {key: 'novaAI.stageRepairs', plain: 'Finding your repairs...'},
  show_repairs: {key: 'novaAI.stageRepairs', plain: 'Finding your repairs...'},
  find_repairs: {key: 'novaAI.stageSearchRepairs', plain: 'Searching repairs...'},
  find_repairs_by_work_order: {
    key: 'novaAI.stageSearchRepairs',
    plain: 'Searching repairs...',
    named: 'Work Order {{id}} — searching repairs...',
  },
  show_previous_work_order_repairs: {
    key: 'novaAI.stageSearchRepairs',
    plain: 'Searching repairs...',
    named: 'Work Order {{id}} — searching repairs...',
  },
  get_work_order_status: {
    key: 'novaAI.stageWorkOrder',
    plain: 'Checking the work order...',
    named: 'Work Order {{id}} — checking status...',
  },
  read_notes: {
    key: 'novaAI.stageNotes',
    plain: 'Reading the notes...',
    named: 'Repair {{id}} — reading the notes...',
  },
  get_part_requests: {
    key: 'novaAI.stagePartRequests',
    plain: 'Checking the part requests...',
    named: 'Repair {{id}} — checking part requests...',
  },
  list_my_part_requests: {key: 'novaAI.stagePartRequests', plain: 'Checking the part requests...'},
  get_configured_parts: {key: 'novaAI.stageParts', plain: 'Looking up parts...'},
  search_parts: {key: 'novaAI.stageParts', plain: 'Looking up parts...'},
  read_part_search: {key: 'novaAI.stageParts', plain: 'Looking up parts...'},
  get_parts_messages: {key: 'novaAI.stageMessages', plain: 'Checking your messages...'},
  list_repair_reasons: {key: 'novaAI.stageReasons', plain: 'Loading repair reasons...'},

  // ── writes: usually quick, but they are the ones worth being sure about ──
  add_note: {key: 'novaAI.stageAddNote', plain: 'Adding the note...'},
  begin_repair: {
    key: 'novaAI.stageBeginRepair',
    plain: 'Starting the repair...',
    named: 'Repair {{id}} — starting...',
  },
  complete_repair: {key: 'novaAI.stageCompleteRepair', plain: 'Completing the repair...'},
  change_repair_reason: {key: 'novaAI.stageChangeReason', plain: 'Updating the repair reason...'},
  change_wo_status: {key: 'novaAI.stageChangeWoStatus', plain: 'Updating the work order...'},
  start_shift: {key: 'novaAI.stageStartShift', plain: 'Starting your shift...'},
  end_shift: {key: 'novaAI.stageEndShift', plain: 'Ending your shift...'},
  log_indirect_activity: {key: 'novaAI.stageLogActivity', plain: 'Logging the activity...'},
  save_task: {key: 'novaAI.stageSaveTask', plain: 'Saving the task...'},
  request_part: {key: 'novaAI.stageRequestPart', plain: 'Requesting the part...'},
  request_custom_part: {key: 'novaAI.stageRequestPart', plain: 'Requesting the part...'},
  submit_configured_part_request: {key: 'novaAI.stageRequestPart', plain: 'Requesting the part...'},

  // ── staged edits, screen changes, navigation, canned answers ─────────────
  // Fast enough that the message is usually gone before it is read, but a
  // slow network should still say something truthful rather than "Searching".
  stage_task_comment: {key: 'novaAI.stageUpdating', plain: 'Updating the task...'},
  stage_task_resolution: {key: 'novaAI.stageUpdating', plain: 'Updating the task...'},
  clear_task_comment: {key: 'novaAI.stageUpdating', plain: 'Updating the task...'},
  discard_task_change: {key: 'novaAI.stageUpdating', plain: 'Updating the task...'},
  select_configured_part: {key: 'novaAI.stageUpdatingParts', plain: 'Updating the parts list...'},
  set_configured_part_quantity: {key: 'novaAI.stageUpdatingParts', plain: 'Updating the parts list...'},
  clear_repair_filters: {key: 'novaAI.stageFiltering', plain: 'Updating the filters...'},
  filter_part_requests: {key: 'novaAI.stageFiltering', plain: 'Updating the filters...'},
  filter_repairs: {key: 'novaAI.stageFiltering', plain: 'Updating the filters...'},
  switch_view: {key: 'novaAI.stageSwitchView', plain: 'Switching the view...'},
  go_to_page: {key: 'novaAI.stageGoToPage', plain: 'Turning the page...'},
  open_screen: {key: 'novaAI.stageOpening', plain: 'Opening it...'},
  open_create_screen: {key: 'novaAI.stageOpening', plain: 'Opening it...'},
  open_repair: {
    key: 'novaAI.stageOpening',
    plain: 'Opening it...',
    named: 'Repair {{id}} — opening...',
  },
  save_all_tasks: {key: 'novaAI.stageSaveTask', plain: 'Saving the task...'},
  greeting: {key: 'novaAI.stageThinking', plain: 'One moment...'},
  explain_note_editing: {key: 'novaAI.stageThinking', plain: 'One moment...'},
  explain_parts_request_filters: {key: 'novaAI.stageThinking', plain: 'One moment...'},
  list_resolution_options: {key: 'novaAI.stageReasons', plain: 'Loading repair reasons...'},
};

/**
 * The one id a message should name, if any.
 *
 * A repair beats the work order it sits on: a technician asking about tasks
 * recognises "Repair 207541", and naming the work order instead would be
 * true but unhelpful.
 */
function subjectId(stage: NovaStage): string | undefined {
  return stage.repair_id ?? stage.work_order_id ?? stage.asset_id ?? stage.part_id;
}

export function stageLabel(
  stage: NovaStage,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  switch (stage?.stage) {
    case 'understanding':
      // Not "Listening...": the mic flow already puts "Nova is listening.."
      // in this same bubble to mean the microphone is open, and on a typed
      // turn the technician has finished talking. Two meanings, one bubble,
      // and one of them false.
      return t('novaAI.stageUnderstanding', {defaultValue: 'Got it...'});
    case 'planning':
      // "Working out what you need" put the effort on the technician's
      // phrasing. The work is Nova's.
      return t('novaAI.stagePlanning', {defaultValue: 'Thinking...'});
    case 'working': {
      const phrase = stage.tool ? TOOL_PHRASES[stage.tool] : undefined;
      if (!phrase) {
        return t('novaAI.stageWorking', {defaultValue: 'Looking that up...'});
      }
      const id = subjectId(stage);
      // The named variant only when there is both a template and an id — a
      // half-filled sentence ("Reading the tasks on Repair ...") is worse than
      // the plain one.
      if (id && phrase.named) {
        return t(`${phrase.key}Named`, {defaultValue: phrase.named, id});
      }
      return t(phrase.key, {defaultValue: phrase.plain});
    }
    case 'answering':
      // The data is already in hand by now, so the useful thing to say is how
      // much longer — not what is happening.
      return t('novaAI.stageAnswering', {defaultValue: 'Almost there...'});
    default:
      // An unknown stage leaves the bubble saying whatever it last said. A new
      // backend stage must never blank the message.
      return '';
  }
}

/**
 * How long a technician has to stop talking before what they said is sent.
 *
 * Short enough that it feels like being heard, long enough to survive the pause
 * in the middle of "how many parts … have been requested". The recogniser gives
 * no silence event of its own, so this is measured from the last revision it
 * sent — interim ones included, which is what makes it a pause in speech rather
 * than a pause in transcription.
 */
const DICTATION_SILENCE_MS = 1500;

/**
 * How long after Nova stops talking before the microphone opens again.
 *
 * `onend` fires when the audio ends, not when the room goes quiet. In a bay
 * with hard surfaces the tail of the last word is still arriving, and it would
 * be the first thing the recogniser hears.
 */
const SPEECH_TAIL_MS = 400;

const NovaAIChatWindow: React.FC<NovaAIChatWindowProps> = ({
  onClose,
  isExpanded = false,
  onToggleExpand,
  isMinimized = false,
  onToggleMinimize,
}) => {
  const theme = useScreenTheme('novaAIScreen');
  const scheme = useColorScheme();
  const styles = useMemo(() => createNovaAIChatWindowStyles(theme), [theme]);
  const flatListRef = useRef<FlatList>(null);
  const inputRef = useRef<TextInput>(null);

  /**
   * Reliably focuses the chat input after user actions on all platforms.
   * 200 ms lets iOS touch-press animations complete and allows the browser's
   * blur/focus cycle to settle before we programmatically request focus.
   */
  const focusInput = useCallback(() => {
    setTimeout(() => inputRef.current?.focus(), 200);
  }, []);

  /** true when the list is scrolled at (or very near) the bottom */
  const isAtBottomRef = useRef(true);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => flatListRef.current?.scrollToEnd({animated: true}), 60);
  }, []);

  /** Only auto-scroll on content-size changes when the user hasn't scrolled up */
  const handleContentSizeChange = useCallback(() => {
    if (isAtBottomRef.current) {
      setTimeout(() => flatListRef.current?.scrollToEnd({animated: false}), 60);
    }
  }, []);

  /** Track whether the user is near the bottom as they scroll */
  const handleScroll = useCallback((event: any) => {
    const {layoutMeasurement, contentOffset, contentSize} = event.nativeEvent;
    const distanceFromBottom =
      contentSize.height - contentOffset.y - layoutMeasurement.height;
    // Treat within 50 px of the bottom as "at bottom"
    isAtBottomRef.current = distanceFromBottom <= 50;
  }, []);
  const {t} = useTranslation();

  // Generate a fresh session UUID when this component mounts.
  // NovaAIChatWindow unmounts on close (conditional render in NovaAI.web.tsx),
  // so each open/close cycle automatically produces a new session_id.
  const sessionIdRef = useRef<string>(uuid.v4() as string);

  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [text, setText] = useState('');

  // ── Live dictation (web) ─────────────────────────────────────────────────
  // Words appear in the input box as they are spoken, and the technician sends
  // them. Deliberately NOT straight to Nova: a platform recogniser cannot be
  // taught part numbers, so "555-123" comes back however it comes back, and
  // the technician has to be able to see and fix it before it is asked.
  //
  // Off unless NOVA_AI_LIVE_TRANSCRIPTION says otherwise, and unavailable
  // where the browser has no Web Speech API — Firefox — in which case the mic
  // keeps the record-and-upload path it has today.
  const dictation = useSpeechToText(() => {});
  const liveDictation =
    Platform.OS === 'web' &&
    Boolean(getConfig()?.NOVA_AI_LIVE_TRANSCRIPTION) &&
    dictation.isSupported;

  // The recogniser revises what it heard, so the box follows it rather than
  // being appended to: the last word changes as often as it is added to.
  useEffect(() => {
    if (liveDictation && dictation.isListening) {
      setText(dictation.recognizedText);
    }
  }, [liveDictation, dictation.isListening, dictation.recognizedText]);

  // A turn is in flight. performBotSearch finds its own message by
  // `placeholder !== ''`, so two overlapping turns would both match the same
  // bubble and the answers would land on the wrong ones. Hands-free makes that
  // reachable in a way tapping send never did.
  const answeringRef = useRef(false);
  // The speaking listener is registered once; this keeps it pointed at the
  // current hook rather than the one that existed at mount.
  const dictationRef = useRef(dictation);
  dictationRef.current = dictation;
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spokenRef = useRef('');
  spokenRef.current = dictation.recognizedText;

  // ── Don't listen to ourselves ────────────────────────────────────────────
  // With the speaker on, the microphone hears Nova's answer, transcribes it,
  // and submit-on-pause asks it straight back. The Web Speech API owns its own
  // audio capture and gives no way to apply echo cancellation to it, so the
  // recogniser cannot be taught to tell Nova's voice from the technician's.
  // Half duplex is the honest fix: closed while talking, open the rest of the
  // time.
  const resumeAfterSpeechRef = useRef(false);
  useEffect(() => {
    if (!liveDictation) {
      return;
    }
    const sub = NovaVoiceModule.addSpeakingListener?.((speaking: boolean) => {
      if (speaking) {
        // Only worth resuming if it was listening to begin with — the mic may
        // simply be off.
        resumeAfterSpeechRef.current = dictationRef.current.isListening;
        if (dictationRef.current.isListening) {
          dictationRef.current.stopListening();
          setRecording(false);
        }
        return;
      }
      if (!resumeAfterSpeechRef.current) {
        return;
      }
      resumeAfterSpeechRef.current = false;
      // A beat after the audio ends, so the tail of the sentence coming back
      // off the walls is not the first thing the recogniser hears.
      setTimeout(() => {
        dictationRef.current.startListening();
        setRecording(true);
        setStatus(statuses?.listening);
      }, SPEECH_TAIL_MS);
    });
    return () => sub?.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveDictation]);

  // ── Submit on a pause ────────────────────────────────────────────────────
  // Hands-free: stop talking and the question goes, the box clears, and the
  // recogniser keeps listening for the next one. The timer is re-armed on every
  // revision — including the interim ones, which arrive while a word is still
  // being said — so it only fires once the technician has actually stopped.
  useEffect(() => {
    if (!liveDictation || !dictation.isListening) {
      return;
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
    }
    if (!dictation.recognizedText.trim()) {
      return;
    }
    silenceTimerRef.current = setTimeout(() => {
      const heard = spokenRef.current.trim();
      if (!heard) {
        return;
      }
      if (answeringRef.current) {
        // Nova is still answering the last one. Hold what was heard and let
        // the next revision re-arm the timer rather than dropping the question
        // or stacking it on top of a turn already running.
        return;
      }
      dictation.reset();
      onSendPress(heard);
    }, DICTATION_SILENCE_MS);

    return () => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveDictation, dictation.isListening, dictation.recognizedText]);
  const [recording, setRecording] = useState(false);
  const [status, setStatus] = useState<Status>('');

  // ── Speaker / TTS toggle ──────────────────────────────────────────────────
  const [isVolumeOn, setIsVolumeOn] = useState(false);

  // ── Suggested questions (right panel — loaded on first expand) ────────────
  const [suggestedQuestions, setSuggestedQuestions] = useState<
    SuggestedQuestion[]
  >([]);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);

  const {sendTextToBackend, sendAudioToBackend, fetchSuggestedQuestions} =
    useNovaAIChatWindowController();
  const {handleNavigation} = useNovaAINavigation();
  const dispatch = useDispatch();
  // A data-change action from Nova ("tasks_updated", "repair_updated",
  // "status_updated") becomes a refresh of whatever is on screen — the open
  // repair, the visible list, or the header status chip — and nothing else.
  // See useNovaDataRefresh for the rules.
  const {applyDataAction} = useNovaDataRefresh();

  // Fetch suggested questions when expanded for the first time
  useEffect(() => {
    if (!isExpanded) return;
    if (suggestedQuestions.length > 0) return; // already loaded
    setIsSuggestionsLoading(true);
    fetchSuggestedQuestions()
      .then(({questions}) => setSuggestedQuestions(questions))
      .catch(() => {
        /* silently ignore */
      })
      .finally(() => setIsSuggestionsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const speakText = useCallback(
    (value: string) => {
      if (isVolumeOn) {
        NovaVoiceModule.speak(value);
      }
    },
    [isVolumeOn],
  );

  // ── Text chat ────────────────────────────────────────────────────────────

  const performBotSearch = useCallback(
    async (userTranscribedText: string) => {
      setStatus('');
      answeringRef.current = true;
      try {
        // Nova reports what it is doing while it works, on the streamed
        // endpoint. The placeholder bubble already stands there saying
        // "Searching..." for three to five seconds, so the progress goes in
        // it — no new UI, and the wait stops looking like nothing happening.
        // On the plain endpoint no stages arrive and the text never changes,
        // which is exactly today's behaviour.
        const onStage = (stage: NovaStage) => {
          const label = stageLabel(stage, t);
          if (!label) {
            return;
          }
          setMessages(prev =>
            prev.map(msg =>
              msg.placeholder !== '' ? {...msg, placeholder: label} : msg,
            ),
          );
        };

        const {reply, listData, actionData, actionType, navAction} =
          await sendTextToBackend(userTranscribedText, sessionIdRef.current, onStage);
        setMessages(prev =>
          prev.map(msg => {
            if (msg.placeholder !== '') {
              return {
                ...msg,
                text: reply,
                placeholder: '',
                listData,
                actionData,
                timestamp: Date.now(),
              };
            }
            return msg;
          }),
        );
        speakText(reply);
        // ── Nova AI navigation intent ────────────────────────────────────
        applyDataAction(actionType, actionData);
        if (navAction) {
          handleNavigation(navAction);
        }
        // ── End Nova AI navigation ───────────────────────────────────────
      } catch {
        setMessages(prev =>
          prev.map(msg => {
            if (msg.placeholder !== '') {
              return {
                ...msg,
                text: t('novaAI.errorFetching'),
                placeholder: '',
                timestamp: Date.now(),
              };
            }
            return msg;
          }),
        );
      } finally {
        answeringRef.current = false;
        setStatus('');
        scrollToBottom();
      }
    },
    [sendTextToBackend, speakText, scrollToBottom, t],
  );

  const onSendPress = (spoken?: string) => {
    // `spoken` is passed by the dictation timer: a callback armed a second ago
    // closes over the `text` of a second ago, which is a word or two behind.
    const trimmed = (typeof spoken === 'string' ? spoken : text).trim();
    if (!trimmed) return;
    setText('');
    const now = Date.now();
    // Force "at bottom" so handleContentSizeChange also scrolls when
    // new messages are rendered — avoids a race between the 60 ms timeout
    // and the FlatList content-size update.
    isAtBottomRef.current = true;
    setMessages(prev => [
      ...prev,
      {
        id: `user-${now}`,
        role: userTypes?.user,
        text: trimmed,
        placeholder: '',
        timestamp: now,
      },
      {
        id: `bot-load-${now}`,
        role: userTypes?.bot,
        text: '',
        placeholder: 'Searching...',
      },
    ]);
    scrollToBottom();
    setTimeout(() => performBotSearch(trimmed), 100);
    // Re-focus input so the user can type the next query immediately
    focusInput();
  };

  // ── Voice chat ───────────────────────────────────────────────────────────

  /**
   * Guard ref – prevents double-processing when both manual-stop and
   * the native AudioRecorderAutoStop event fire for the same recording.
   */
  const isProcessingVoiceRef = useRef(false);

  /**
   * Stable ref to the latest handleVoicePath so the addListener callback
   * (registered once at mount) always calls the current version without
   * the useEffect needing to re-register on every render – same pattern
   * as sendVoiceRef in useVoiceRecorder.
   */
  const handleVoicePathRef = useRef<(path: string) => Promise<void>>(
    async () => {},
  );

  const handleVoicePath = useCallback(
    async (path: string) => {
      // Prevent double-call (e.g. manual stop + auto-stop event both firing)
      if (isProcessingVoiceRef.current) return;
      isProcessingVoiceRef.current = true;

      const now = Date.now();
      // Capture the exact IDs so the update only targets THIS voice exchange
      const voiceUserMsgId = `user-voice-${now}`;
      const voiceBotLoadId = `bot-load-${now}`;
      // Force "at bottom" so handleContentSizeChange also scrolls when the
      // new voice bubbles are rendered (avoids the 60 ms timeout race).
      isAtBottomRef.current = true;
      setMessages(prev => [
        ...prev,
        {
          id: voiceUserMsgId,
          role: userTypes?.user,
          text: '',
          placeholder: '',
          isVoice: true,
          timestamp: now,
        },
        {
          id: voiceBotLoadId,
          role: userTypes?.bot,
          text: '',
          placeholder: 'Nova is listening..',
        },
      ]);
      scrollToBottom();

      try {
        const {reply, listData, actionData, actionType, navAction, transcript} =
          await sendAudioToBackend(path, sessionIdRef.current);
        setMessages(prev =>
          prev.map(msg => {
            // Update only the bot loading bubble for THIS voice exchange
            if (msg.id === voiceBotLoadId) {
              return {
                ...msg,
                text: reply,
                placeholder: '',
                listData,
                actionData,
                timestamp: Date.now(),
              };
            }
            // Replace waveform with transcript only for THIS voice user bubble
            if (msg.id === voiceUserMsgId) {
              return {
                ...msg,
                isVoiceComplete: true,
                transcript: transcript ?? '',
                text: transcript ?? '',
              };
            }
            return msg;
          }),
        );
        speakText(reply);
        // ── Nova AI navigation intent ────────────────────────────────────
        applyDataAction(actionType, actionData);
        if (navAction) {
          handleNavigation(navAction);
        }
        // ── End Nova AI navigation ───────────────────────────────────────
      } catch {
        setMessages(prev =>
          prev.map(msg => {
            if (msg.id === voiceBotLoadId) {
              return {
                ...msg,
                text: t('novaAI.errorFetching'),
                placeholder: '',
                timestamp: Date.now(),
              };
            }
            if (msg.id === voiceUserMsgId) {
              return {...msg, isVoiceComplete: true, text: '', transcript: ''};
            }
            return msg;
          }),
        );
      } finally {
        isProcessingVoiceRef.current = false;
        scrollToBottom();
      }
    },
    [sendAudioToBackend, scrollToBottom, speakText, t],
  );

  // Keep ref in sync with the latest callback version
  handleVoicePathRef.current = handleVoicePath;

  // Register the auto-stop listener ONCE at mount (stable, no re-registration)
  useEffect(() => {
    const sub = NovaVoiceModule.addListener((path: string) => {
      setRecording(false);
      setStatus('');
      if (path) {
        handleVoicePathRef.current(path);
      }
      // Re-focus input immediately after auto-stop so user can type next query
      focusInput();
    });
    return () => sub?.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Mic button handler – same toggle pattern as AddNewNoteModal.
   * 1st tap  → start recording (handles permissions cross-platform)
   * 2nd tap  → stop recording, get path, immediately send to /api/voice
   */
  const handleRecord = async () => {
    // ── Live dictation, where it is available ────────────────────────────
    // A different shape from the recording path below: nothing is uploaded,
    // nothing is sent, and the second tap just stops the recogniser. What was
    // heard is already in the input box for the technician to send.
    if (liveDictation) {
      // Barge-in, such as it can be: the Web Speech API cannot tell Nova's
      // voice from the technician's, so interrupting is a tap rather than
      // simply talking over it. Stopping the speech also releases the
      // microphone the listener above closed.
      NovaVoiceModule.stopSpeaking?.();
      resumeAfterSpeechRef.current = false;

      if (dictation.isListening) {
        await dictation.stopListening();
        setRecording(false);
        setStatus('');
        focusInput();
      } else {
        await dictation.startListening();
        setRecording(true);
        setStatus(statuses?.listening);
      }
      return;
    }

    if (!recording) {
      // ── Start ──────────────────────────────────────────────────────────
      isProcessingVoiceRef.current = false; // reset for new session

      if (Platform.OS === 'web') {
        try {
          await NovaVoiceModule.startListening();
          setRecording(true);
          setStatus(statuses?.listening);
          // Dismiss keyboard / remove cursor while recording is active
          inputRef.current?.blur();
        } catch (e) {
          console.error('Web recording failed:', e);
          Alert.alert(
            'Microphone Permission',
            'Please allow microphone access in your browser.',
          );
        }
      } else {
        // Android / iOS
        let currentPermission = await checkMicrophonePermission();

        if (currentPermission !== RESULTS.GRANTED) {
          currentPermission = await requestMicrophonePermission();
          if (currentPermission === RESULTS.GRANTED) {
            // Just granted – let user tap again intentionally
            return;
          }
        }

        if (currentPermission === RESULTS.BLOCKED) {
          Alert.alert(
            'Microphone Permission',
            'Microphone access is blocked. Please enable it in your device settings.',
          );
          return;
        }

        if (currentPermission !== RESULTS.GRANTED) {
          Alert.alert(
            'Microphone Permission',
            'This app needs access to your microphone to record audio.',
          );
          return;
        }

        try {
          await NovaVoiceModule.startListening();
          setRecording(true);
          setStatus(statuses?.listening);
          // Dismiss keyboard / remove cursor while recording is active
          inputRef.current?.blur();
        } catch (e) {
          console.error('Recording failed:', e);
        }
      }
    } else {
      // ── Stop (2nd tap) ─────────────────────────────────────────────────
      const path = await NovaVoiceModule.stopListening();
      setRecording(false);
      setStatus('');

      // On mobile, AudioRecorderAutoStop fires only on silence auto-stop,
      // not on a manual tap-to-stop, so we process the path directly here.
      // On web, onstop notifies our addListener callback (handleVoicePathRef)
      // which handles it; we skip to avoid a double-send. The isProcessing
      // guard also prevents double-sends if both paths fire simultaneously.
      if (path) {
        handleVoicePathRef.current(path);
      }
      // Re-focus input immediately after mic release so user can type next query
      focusInput();
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const renderItem = ({item}: {item: ChatMsg}) => {
    const isUser = item.role === userTypes?.user;
    const isStructured =
      !isUser && item.placeholder === '' && !!item.listData?.columns?.length;
    return (
      <View
        style={[
          styles.chatRow,
          isUser ? styles.chatRowUser : styles.chatRowBot,
        ]}>
        {!isUser && (
          <View style={styles.inlineBotIcon}>
            {/* <Image
              source={require('../../assets/icons/BotIcon.png')}
              resizeMode="contain"
              style={styles.inlineBotIconImage}
            /> */}
            <NovaAIChatWindowIcon />
          </View>
        )}
        <View
          style={[
            styles.message,
            isUser && styles.botMessageView,
            styles.chatMessage,
            isUser ? styles.userMessage : styles.botMessage,
            item.placeholder !== '' && {borderWidth: 0},
            isStructured && styles.structuredMessageWrapper,
          ]}>
          {isUser && item.isVoice ? (
            item.isVoiceComplete ? (
              <Text style={styles.messageText}>
                {item.transcript || item.text}
              </Text>
            ) : (
              <VoiceWaveformBubble
                waveformContainerStyle={styles.waveformContainer}
                waveformBarStyle={styles.waveformBar}
                isActive={true}
              />
            )
          ) : item.placeholder !== '' ? (
            <Text
              style={[
                isUser ? styles.messageText : styles.botMessageText,
                styles.placeHolderStyles,
              ]}>
              {item.placeholder}
            </Text>
          ) : !isUser ? (
            <BotStructuredMessage
              text={item.text}
              listData={item.listData}
              styles={styles}
            />
          ) : (
            <Text style={styles.messageText}>{item.text}</Text>
          )}
          {/* Timestamp — shown only when message content is ready */}
          {item.placeholder === '' && item.timestamp ? (
            <Text
              style={[
                styles.messageTimestamp,
                isUser && styles.messageTimestampUser,
              ]}>
              {new Date(item.timestamp)
                .toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })
                .toUpperCase()}
            </Text>
          ) : null}
        </View>
      </View>
    );
  };

  const getMessages = () => (
    <View style={styles.listWrapper}>
      <FlatList
        ref={flatListRef}
        data={messages}
        keyExtractor={item => item.id}
        renderItem={renderItem}
        contentContainerStyle={styles.list}
        onContentSizeChange={handleContentSizeChange}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        extraData={messages}
      />
    </View>
  );

  const getNovaAssistance = () => (
    <ScrollView
      style={styles.assistanceScroll}
      contentContainerStyle={styles.iconWrapper}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled">
      <View style={styles.titleMainContainer}>
        <View style={styles.iconBackground}>
          {/* <Image
            source={
              scheme === 'dark'
                ? require('../../assets/icons/BotIcon.png')
                : require('../../assets/icons/BotIcon.png')
            }
            resizeMode="contain"
            style={styles.botIcon}
          /> */}
          <NovaAIChatBotIcon />
        </View>
        <View style={styles.titlesContainer}>
          <Text style={styles.title}>{t('novaAI.text')}</Text>
          <Text style={styles.subtitle}>{t('novaAI.subText')}</Text>
        </View>
        <View style={styles.listContainer}>
          {novaAICardData.map(card => (
            <View key={card.id} style={styles.card}>
              <card.icon />
              <View style={styles.cardTextWrapper}>
                <Text style={styles.cardTitle}>{t(card.title)}</Text>
                <Text style={styles.cardExample}>{t(card.example)}</Text>
              </View>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  // ── Suggested Questions right panel ───────────────────────────────────────
  const getSuggestedPanel = () => (
    <View style={styles.suggestedPanel}>
      {isSuggestionsLoading ? (
        <View style={styles.suggestedPanelLoading}>
          <ActivityIndicator size="small" />
          <Text style={styles.suggestedPanelLoadingText}>
            {t('novaAI.panelLoading')}
          </Text>
        </View>
      ) : (
        <ScrollView showsVerticalScrollIndicator={false}>
          <Text style={styles.suggestedPanelTitle}>
            {t('novaAI.suggestedPanelTitle')}
          </Text>

          <View style={styles.suggestedQuestions}>
            {suggestedQuestions.map(item => {
              const CategoryIcon = CATEGORY_ICON_MAP[item.iconType];
              return (
                <View key={item.id} style={styles.suggestedCard}>
                  <View style={styles.suggestedCardCategoryRow}>
                    <CategoryIcon size={14} color={item.categoryColor} />
                    <Text
                      style={[
                        styles.suggestedCardCategoryText,
                        {color: item.categoryColor},
                      ]}>
                      {item.category}
                    </Text>
                  </View>
                  <Text style={styles.suggestedCardQuestion}>
                    {item.question}
                  </Text>
                </View>
              );
            })}
          </View>

          <View style={styles.capabilitySection}>
            <Text style={styles.capabilitySectionTitle}>
              {t('novaAI.novaCanHelpWith')}
            </Text>
            {NOVA_CAPABILITIES.map((cap, idx) => (
              <View key={idx} style={styles.capabilityItem}>
                <View style={styles.capabilityCheckIcon}>
                  <NovaCategoryCheckIcon />
                </View>
                <Text style={styles.capabilityText}>{cap}</Text>
              </View>
            ))}
          </View>
        </ScrollView>
      )}
    </View>
  );

  return (
    <View style={styles.modalView}>
      {/* Header – always full width */}
      <View style={styles.headerView}>
        <View style={styles.leftHeaderView}>
          <View style={styles.inlineBotIcon}>
            <NovaAIChatWindowIcon />
          </View>
          <Text style={styles.headerText}>{t('novaAI.header')}</Text>
        </View>
        <View style={styles.headerRightActions}>
          {/* Volume and expand buttons — hidden when minimized */}
          {!isMinimized && (
            <TouchableOpacity
              onPress={() => setIsVolumeOn(prev => !prev)}
              testID="volumeIconId"
              accessibilityRole="button"
              accessibilityLabel={t('novaAI.header')}>
              {isVolumeOn ? (
                <NovaAIVolumeIcon color={(theme as any).volumeIconColor} />
              ) : (
                <NovaAIVolumeMuteIcon color={(theme as any).volumeIconColor} />
              )}
            </TouchableOpacity>
          )}
          {/* {!isMinimized && (
            <TouchableOpacity
              onPress={onToggleExpand}
              testID="expandCollapseIconId"
              accessibilityRole="button">
              {isExpanded ? <NovaAICollapseIcon /> : <NovaAIExpandIcon />}
            </TouchableOpacity>
          )} */}
          {/* Minimize (collapse) / Restore (expand) */}
          <TouchableOpacity
            onPress={onToggleMinimize}
            testID="minimizeIconId"
            accessibilityRole="button"
            accessibilityLabel={
              isMinimized ? 'Restore chat window' : 'Minimize chat window'
            }>
            {isMinimized ? (
              <NovaAIMinimizeCollapseIcon color={(theme as any).volumeIconColor} />
            ) : (
              <NovaAIMinimizeExpandIcon color={(theme as any).volumeIconColor} />
            )}
          </TouchableOpacity>
          <TouchableOpacity
            onPress={onClose}
            testID="closeIconId"
            accessibilityRole="button">
            <CloseIcon color={(theme as any).volumeIconColor} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Body and input — hidden when minimized */}
      {!isMinimized && (
        <>
          {/* Body: single column (normal) or chat + suggested panel (expanded) */}
          <View style={isExpanded ? styles.expandedBody : styles.normalBody}>
            {/* Chat column */}
            <View
              style={isExpanded ? styles.chatColumn : styles.chatColumnFull}>
              {messages?.length > 0 && <View style={styles.lineSeparator} />}
              {messages?.length > 0 ? getMessages() : getNovaAssistance()}
            </View>

            {/* Right panel — only when expanded */}
            {isExpanded && getSuggestedPanel()}
          </View>

          {/* Input and footer – always full width */}
          <View style={styles.inputContainer}>
            <Input
              customStyles={styles.input}
              containerStyle={styles.textContainer}
              placeholder={
                recording ? t('novaAI.listening') : t('novaAI.askNova')
              }
              value={text}
              onChangeText={setText}
              onSubmitEditing={() => onSendPress()}
              returnKeyType="send"
              submitBehavior="submit"
              inputRef={inputRef}
            />
            <TouchableOpacity
              style={[styles.micBtn, recording && styles.micBtnRecording]}
              onPress={handleRecord}
              testID="micIConId">
              {recording ? (
                <MicIcon
                  color={(theme as any).voiceIconBlueColor}
                  fillColor={(theme as any).voiceIconBlueFillColor}
                />
              ) : (
                <MicIcon
                  color={(theme as any).voiceIconGreyColor}
                  fillColor={(theme as any).voiceIconGreyFillColor}
                />
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.sendButton}
              onPress={() => onSendPress()}
              testID="sendIconId">
              {text?.length > 0 ? (
                <SendIcon
                  color={(theme as any).voiceIconBlueColor}
                  fillColor={(theme as any).voiceIconBlueFillColor}
                />
              ) : (
                <SendIcon
                  color={(theme as any).voiceIconGreyColor}
                  fillColor={(theme as any).voiceIconGreyFillColor}
                />
              )}
            </TouchableOpacity>
          </View>
        </>
      )}
    </View>
  );
};

export default NovaAIChatWindow;
